import { randomUUID } from './uuid.js';
import type {
  AfbeeldingItem,
  AudioItem,
  ProjectModel,
  TekstItem,
  TimelineItem,
  Track,
  TrackType,
  VideoItem,
} from './projectmodel.js';
import { berekenDuurInFrames, itemEindFrame } from './timeline.js';

/**
 * Montagebewerkingen op het projectmodel.
 *
 * Alle functies zijn puur: ze krijgen een project en geven een nieuw project
 * terug, zonder het origineel aan te passen. Daardoor kan undo/redo met
 * goedkope verwijzingen werken en kan er nooit een halve wijziging ontstaan.
 */

export function kloonProject(project: ProjectModel): ProjectModel {
  return structuredClone(project);
}

function metTrack(project: ProjectModel, trackId: string, fn: (track: Track) => Track): ProjectModel {
  return {
    ...project,
    tracks: project.tracks.map((t) => (t.id === trackId ? fn(t) : t)),
  };
}

export function voegItemToe(project: ProjectModel, trackId: string, item: TimelineItem): ProjectModel {
  return metTrack(project, trackId, (track) => ({ ...track, items: [...track.items, item] }));
}

export function verwijderItem(project: ProjectModel, itemId: string): ProjectModel {
  return {
    ...project,
    tracks: project.tracks.map((track) => ({
      ...track,
      items: track.items.filter((i) => i.id !== itemId),
    })),
  };
}

export function werkItemBij(
  project: ProjectModel,
  itemId: string,
  wijziging: Partial<TimelineItem>,
): ProjectModel {
  return {
    ...project,
    tracks: project.tracks.map((track) => ({
      ...track,
      items: track.items.map((i) => (i.id === itemId ? ({ ...i, ...wijziging } as TimelineItem) : i)),
    })),
  };
}

/** Vindt een item en zijn track. */
export function zoekItem(project: ProjectModel, itemId: string): { track: Track; item: TimelineItem } | null {
  for (const track of project.tracks) {
    const item = track.items.find((i) => i.id === itemId);
    if (item) return { track, item };
  }
  return null;
}

export function verplaatsItem(
  project: ProjectModel,
  itemId: string,
  naarTrackId: string,
  startFrame: number,
): ProjectModel {
  const gevonden = zoekItem(project, itemId);
  if (!gevonden) return project;
  const nieuweStart = Math.max(0, Math.round(startFrame));
  if (gevonden.track.id === naarTrackId) {
    return metTrack(project, naarTrackId, (track) => ({
      ...track,
      items: track.items.map((i) => (i.id === itemId ? { ...i, startFrame: nieuweStart } : i)),
    }));
  }
  const zonder = verwijderItem(project, itemId);
  return metTrack(zonder, naarTrackId, (track) => ({
    ...track,
    items: [...track.items, { ...gevonden.item, startFrame: nieuweStart }],
  }));
}

export interface SplitResultaat {
  project: ProjectModel;
  links: TimelineItem;
  rechts: TimelineItem;
}

/**
 * Splitst een item op een tijdlijnframe in twee items.
 *
 * Voor video en audio wordt de trim-offset van het rechterdeel opgehoogd,
 * zodat het rechterdeel doorgaat waar het linker deel ophield. Dat is precies
 * wat een gebruiker verwacht bij knippen.
 */
export function splitItem(project: ProjectModel, itemId: string, opFrame: number): SplitResultaat | null {
  const gevonden = zoekItem(project, itemId);
  if (!gevonden) return null;
  const { track, item } = gevonden;
  const frame = Math.round(opFrame);
  if (frame <= item.startFrame || frame >= itemEindFrame(item)) return null;

  const linksDuur = frame - item.startFrame;
  const rechtsDuur = item.duurInFrames - linksDuur;

  const links = { ...item, duurInFrames: linksDuur } as TimelineItem;
  const rechts = {
    ...item,
    id: randomUUID(),
    startFrame: frame,
    duurInFrames: rechtsDuur,
  } as TimelineItem;

  if ('bronStartFrame' in links && 'bronStartFrame' in rechts) {
    const snelheid = 'snelheid' in links ? ((links as VideoItem).snelheid ?? 1) : 1;
    (rechts as VideoItem | AudioItem).bronStartFrame =
      (links as VideoItem | AudioItem).bronStartFrame + Math.round(linksDuur * snelheid);
    // Fades horen bij het uiteinde van de clip, niet bij de knip.
    if ('fadeInFrames' in links) (links as VideoItem).fadeOutFrames = 0;
    if ('fadeInFrames' in rechts) (rechts as VideoItem).fadeInFrames = 0;
  }
  if (rechts.soort === 'video') {
    (rechts as VideoItem).overgangIn = 'geen';
    (rechts as VideoItem).overgangDuurInFrames = 0;
  }

  const project2 = metTrack(project, track.id, (t) => ({
    ...t,
    items: t.items.flatMap((i) => (i.id === itemId ? [links, rechts] : [i])),
  }));
  return { project: project2, links, rechts };
}

export function dupliceerItem(project: ProjectModel, itemId: string, offsetFrames = 0): ProjectModel | null {
  const gevonden = zoekItem(project, itemId);
  if (!gevonden) return null;
  const kopie: TimelineItem = {
    ...structuredClone(gevonden.item),
    id: randomUUID(),
    startFrame: Math.max(0, gevonden.item.startFrame + offsetFrames),
    naam: gevonden.item.naam ? `${gevonden.item.naam} (kopie)` : '',
  };
  return voegItemToe(project, gevonden.track.id, kopie);
}

/** Standaardduur voor een item waarvoor de gebruiker niets koos. */
export function standaardDuur(type: TrackType): number {
  if (type === 'tekst') return 90; // 3 seconden bij 30 fps
  if (type === 'afbeelding') return 150; // 5 seconden
  return 150;
}

export function maakTekstItem(opties: Partial<TekstItem> & { startFrame: number }): TekstItem {
  return {
    id: randomUUID(),
    soort: 'tekst',
    naam: opties.naam ?? 'Tekst',
    startFrame: Math.max(0, Math.round(opties.startFrame)),
    duurInFrames: opties.duurInFrames ?? 90,
    tekst: opties.tekst ?? 'Nieuwe tekst',
    x: opties.x ?? 0.5,
    y: opties.y ?? 0.8,
    fontFamily: opties.fontFamily ?? 'Inter',
    fontSize: opties.fontSize ?? 64,
    fontWeight: opties.fontWeight ?? 700,
    kleur: opties.kleur ?? '#ffffff',
    uitlijning: opties.uitlijning ?? 'midden',
    schaduw: opties.schaduw ?? true,
    achtergrondKleur: opties.achtergrondKleur ?? 'transparant',
    maxBreedte: opties.maxBreedte ?? 0.8,
    dekking: opties.dekking ?? 1,
    rotatie: opties.rotatie ?? 0,
    animatie: opties.animatie ?? 'geen',
    vergrendeld: opties.vergrendeld ?? false,
    verborgen: opties.verborgen ?? false,
  };
}

export function maakVideoItem(opties: Partial<VideoItem> & { assetId: string; startFrame: number }): VideoItem {
  return {
    id: randomUUID(),
    soort: 'video',
    naam: opties.naam ?? 'Clip',
    assetId: opties.assetId,
    startFrame: Math.max(0, Math.round(opties.startFrame)),
    duurInFrames: opties.duurInFrames ?? 150,
    bronStartFrame: opties.bronStartFrame ?? 0,
    passing: opties.passing ?? 'bevatten',
    volume: opties.volume ?? 1,
    gedempt: opties.gedempt ?? false,
    fadeInFrames: opties.fadeInFrames ?? 0,
    fadeOutFrames: opties.fadeOutFrames ?? 0,
    overgangIn: opties.overgangIn ?? 'geen',
    overgangDuurInFrames: opties.overgangDuurInFrames ?? 0,
    snelheid: opties.snelheid ?? 1,
    vergrendeld: opties.vergrendeld ?? false,
    verborgen: opties.verborgen ?? false,
  };
}

export function maakAfbeeldingItem(
  opties: Partial<AfbeeldingItem> & { assetId: string; startFrame: number },
): AfbeeldingItem {
  return {
    id: randomUUID(),
    soort: 'afbeelding',
    naam: opties.naam ?? 'Afbeelding',
    assetId: opties.assetId,
    startFrame: Math.max(0, Math.round(opties.startFrame)),
    duurInFrames: opties.duurInFrames ?? 150,
    passing: opties.passing ?? 'bevatten',
    x: opties.x ?? 0.5,
    y: opties.y ?? 0.5,
    schaal: opties.schaal ?? 1,
    beweging: opties.beweging ?? 'geen',
    rotatie: opties.rotatie ?? 0,
    dekking: opties.dekking ?? 1,
    vergrendeld: opties.vergrendeld ?? false,
    verborgen: opties.verborgen ?? false,
  };
}

export function maakAudioItem(opties: Partial<AudioItem> & { assetId: string; startFrame: number }): AudioItem {
  return {
    id: randomUUID(),
    soort: 'audio',
    naam: opties.naam ?? 'Audio',
    assetId: opties.assetId,
    startFrame: Math.max(0, Math.round(opties.startFrame)),
    duurInFrames: opties.duurInFrames ?? 300,
    bronStartFrame: opties.bronStartFrame ?? 0,
    volume: opties.volume ?? 0.6,
    gedempt: opties.gedempt ?? false,
    fadeInFrames: opties.fadeInFrames ?? 15,
    fadeOutFrames: opties.fadeOutFrames ?? 30,
    lus: opties.lus ?? false,
    vergrendeld: opties.vergrendeld ?? false,
    verborgen: opties.verborgen ?? false,
  };
}

/**
 * Zet de items op een track op volgorde zonder gaten, vanaf frame 0.
 * Wordt gebruikt door het hulpmiddel "sluit gaten".
 */
export function sluitGaten(project: ProjectModel, trackId: string): ProjectModel {
  return metTrack(project, trackId, (track) => {
    let cursor = 0;
    const items = [...track.items]
      .sort((a, b) => a.startFrame - b.startFrame)
      .map((item) => {
        const nieuw = { ...item, startFrame: cursor };
        cursor += item.duurInFrames;
        return nieuw;
      });
    return { ...track, items };
  });
}

/** Duur in seconden, afgerond op twee decimalen. */
export function duurInSeconden(project: ProjectModel): number {
  return Math.round((berekenDuurInFrames(project) / project.fps) * 100) / 100;
}

export * from './historie.js';
